import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch, RootState } from "../../store"
import { useTranslation } from "react-i18next"
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts"
import { TrendingUp, Calendar, DollarSign } from "lucide-react"
import { FiSearch, FiCreditCard, FiCalendar } from "react-icons/fi"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { getStatusClass } from "../../utils/data"
import { Link } from "react-router-dom"
import ScrollButton from "../ui/ScrollButton"
import { BookingTrip } from "../../types"
import { fetchAllBookTrips, filterAdminTrips } from "../../features/admin/adminTripBookingSlice"

const AdminCard = ({ title, icon: Icon, children }: any) => (
    <div className="bg-white rounded-2xl shadow-md p-5 flex flex-col gap-4 w-full">
        <div className="flex items-center justify-between border-b pb-2">
            <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                <Icon className="w-5 h-5 text-gray-500" />
                {title}
            </h3>
        </div>
        <div>{children}</div>
    </div>
)

const TripBooks = () => {
    const { t } = useTranslation()
    const dispatch = useDispatch<AppDispatch>()
    const { filterBookingResult, tripBookings } = useSelector((state: RootState) => state.adminTripBookingSlice)

    const [search, setSearch] = useState("")
    const [statusFilter, setStatusFilter] = useState("all")
    const [checkInFilter, setCheckInFilter] = useState<Date | null>(null)
    const [checkOutFilter, setCheckOutFilter] = useState<Date | null>(null)

    let displayedBookings =
        filterBookingResult.length > 0 ? filterBookingResult : tripBookings

    useEffect(() => {
        dispatch(fetchAllBookTrips())
    }, [dispatch])

    const totalBookings = displayedBookings.length
    const totalRevenue = displayedBookings.reduce((acc, b) => {
        if (b.state === "Approved" || b.state === "Paid") {
            return acc + (b.price || 0)
        }
        return acc
    }, 0)

    const upcoming = displayedBookings.filter(
        (b) => new Date(b.trip.fromDate) > new Date()
    ).length

    const pieData = [
        { name: t("upcoming"), value: upcoming },
        { name: t("completed"), value: totalBookings - upcoming },
    ]

    const statusCounts = displayedBookings.reduce((acc: any, b: any) => {
        acc[b.status] = (acc[b.status] || 0) + 1
        return acc
    }, {})

    const statusData = Object.keys(statusCounts).map((status) => ({
        name: t(status),
        value: statusCounts[status],
    }))

    const COLORS = ["#bfc6cc", "#007118"]
    const STATUS_COLORS = [
        "#007118",
        "#b91c1c",
        "#f59e0b",
        "#3b82f6",
        "#6b7280",
        "#8b5cf6",
    ]

    // فلترة بالبحث والحالة
    displayedBookings = displayedBookings.filter((booking: any) => {
        const matchSearch =
            booking.userName?.toLowerCase().includes(search?.toLowerCase()) ||
            booking.id.toString().includes(search)
        const matchStatus =
            statusFilter === "all" ? true : booking.status === statusFilter
        return matchSearch && matchStatus
    })

    // فلترة من الباك
    const handleFilterSubmit = () => {
        dispatch(
            filterAdminTrips({
                type: "Trip",
                fromDate: checkInFilter ? checkInFilter.toISOString() : undefined,
                toDate: checkOutFilter ? checkOutFilter.toISOString() : undefined,
            })
        )
    }

    return (
        <div>
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-500 text-white rounded-xl shadow-md p-6 mb-6">
                <h2 className="text-white text-lg font-semibold">{t("trip-books")}</h2>
                <p className="text-sm opacity-80 mt-1">
                    {t("total-bookings")}: {displayedBookings.length}
                </p>
            </div>

            {/* Analytics */}
            <div className="grid md:grid-cols-3 mb-5 gap-6 w-full">
                <AdminCard title={t("totalBookings")} icon={TrendingUp}>
                    <p className="text-2xl font-bold text-gray-900">{totalBookings}</p>
                </AdminCard>

                <AdminCard title={t("upcoming")} icon={Calendar}>
                    <p className="text-2xl font-bold text-blue-600">{upcoming}</p>
                </AdminCard>

                <AdminCard title={t("revenue")} icon={DollarSign}>
                    <p className="text-2xl font-bold text-primary">
                        LYD {totalRevenue.toLocaleString()}
                    </p>
                </AdminCard>
            </div>

            {/* Charts */}
            <AdminCard title={t("bookingsOverview")} icon={TrendingUp}>
                <div className="grid xs:grid-cols-1 sm:grid-cols-2">
                    <div className="h-60">
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    dataKey="value"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={90}
                                    label
                                >
                                    {pieData.map((_, index) => (
                                        <Cell key={index} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="h-60">
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={statusData}
                                    dataKey="value"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={90}
                                    label
                                >
                                    {statusData.map((_, index) => (
                                        <Cell key={index} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </AdminCard>

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm p-4 my-5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder={t("search-user") as string}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 pr-3 py-2 border rounded-lg w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>

                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                    <option value="all">{t("All Statuses")}</option>
                    <option value="Pending">{t("Pending")}</option>
                    <option value="Approved">{t("Approved")}</option>
                    <option value="Returned">{t("Returned")}</option>
                    <option value="Cancelled">{t("Cancelled")}</option>
                    <option value="Paid">{t("Paid")}</option>
                    <option value="Waiting">{t("Waiting")}</option>
                </select>

                <DatePicker
                    selected={checkInFilter}
                    onChange={(date) => setCheckInFilter(date)}
                    placeholderText={t("check-in") as string}
                    className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    dateFormat="yyyy-MM-dd"
                />
                <DatePicker
                    selected={checkOutFilter}
                    onChange={(date) => setCheckOutFilter(date)}
                    placeholderText={t("check-out") as string}
                    className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    dateFormat="yyyy-MM-dd"
                />

                <button
                    onClick={handleFilterSubmit}
                    className="bg-primary hover:bg-[#00470f] text-white px-4 py-2 rounded-lg text-sm"
                >
                    {t("search")}
                </button>
            </div>

            {/* Bookings List */}
            <div className="grid rounded-xl bg-white mt-5 p-4 gap-6 w-full">
                {displayedBookings.map((booking: BookingTrip) => (
                    <div
                        key={booking.id}
                        className="flex items-center justify-between border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-200"
                    >
                        <div className="flex items-center gap-4">
                            <img
                                src={booking.trip?.tripImages[0]?.url || "/placeholder.jpg"}
                                alt={booking.trip?.toLocation}
                                className="w-16 h-16 rounded-lg object-cover"
                            />
                            <div>
                                <Link
                                    to={`/dashboard/trip-booking-report/${booking.trip?.id}`}
                                    className="font-semibold text-gray-800"
                                >
                                    {t("trip")} {booking.trip?.fromLocation} {t("to")} {booking.trip?.toLocation}
                                </Link>

                                <p className="text-xs text-textSecond">
                                    {t("bookingId")}: #{booking.id}
                                </p>
                                <Link
                                    to={`/dashboard/user-trip-booking-report/${booking.userId}`}
                                    className="text-xs text-textSecond"
                                >
                                    {t("user")}: {booking.userName}
                                </Link>
                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                    <FiCalendar /> {new Date(booking.trip.fromDate).toLocaleDateString()} →{" "}
                                    {new Date(booking.trip.toDate).toLocaleDateString()}
                                </p>
                            </div>
                        </div>

                        {/* الحالة + السعر */}
                        <div className="flex flex-col text-right">
                            <span
                                className={`px-3 py-1 rounded-full text-xs font-medium self-end ${getStatusClass(
                                    booking.state
                                )}`}
                            >
                                {t(booking.state)}
                            </span>
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                                <FiCreditCard /> {booking.currency} {booking.price.toFixed(2)}
                            </p>
                        </div>

                        <Link
                            className="ml-4 px-4 py-2 text-sm bg-primary hover:bg-[#004e11] text-white rounded-lg"
                            to={`/dashboard/trip-book-details/${booking.id}`} 
                        >
                            {t("view")}
                        </Link>
                    </div>
                ))}
            </div>

            <ScrollButton />
        </div>
    )
}

export default TripBooks
